import type { Item } from '@dissidium/contracts';
import type { ActionDefinition, ActionId, CapabilityDefinition, CapabilityId, ItemDefinition, ItemTemplateId, Origin, OriginShape, StartingKitDefinition, StartingKitId } from './types.ts';

export const PROVENANCE_SEPARATOR = '@';
const SLUG = /^[a-z0-9-]+$/;

/** Unarmed and universal moves every living actor can attempt. */
export const BASE_ACTIONS: readonly ActionId[] = ['advance', 'strike', 'withdraw'];

export const ACTIONS: readonly ActionDefinition[] = [
  { id: 'advance', label: 'Advance', kind: 'maneuver' },
  { id: 'strike', label: 'Basic strike', kind: 'melee' },
  { id: 'withdraw', label: 'Withdraw', kind: 'maneuver' },
  { id: 'thrust', label: 'Spear thrust', kind: 'melee' },
  { id: 'set-spear', label: 'Set against the charge', kind: 'guard' },
  { id: 'cleave', label: 'Cleaving cut', kind: 'melee' },
  { id: 'loose-arrow', label: 'Loosed arrow', kind: 'ranged' },
  { id: 'shield-brace', label: 'Shield brace', kind: 'guard' },
  { id: 'shield-wall', label: 'Locked shield wall', kind: 'guard' },
  { id: 'riposte', label: 'Riposte', kind: 'melee' },
  { id: 'quick-cut', label: 'Belt-knife cut', kind: 'melee' },
  { id: 'pry-pawl', label: 'Pry the pawl', kind: 'tool' },
  { id: 'set-anchor', label: 'Set the anchor', kind: 'tool' },
  { id: 'prepare-treatment', label: 'Prepare treatment', kind: 'tool' },
  { id: 'read-rite', label: 'Read a rite', kind: 'tool' },
  { id: 'slip-past', label: 'Slip past attention', kind: 'maneuver' },
  { id: 'inspect-mechanism', label: 'Inspect mechanism', kind: 'tool' },
  { id: 'disarm-tether', label: 'Disarm a tether', kind: 'tool' },
];

export const CAPABILITIES: readonly CapabilityDefinition[] = [
  { id: 'brace-drill', label: 'Shield brace drill', grants: ['shield-wall'], requiresSlot: 'off-hand' },
  { id: 'blade-drill', label: 'Blade drill', grants: ['riposte'], requiresSlot: 'main-hand' },
  { id: 'rite-familiarity', label: 'Rite familiarity', grants: ['read-rite'], requiresSlot: null },
  { id: 'fieldcraft', label: 'Practical fieldcraft', grants: ['disarm-tether'], requiresSlot: 'off-hand' },
];

const FOUNDATIONS = 'docs/design.md: Combat, magic & non-combat systems';
const NAMED = 'docs/world/08-magic.md: PROPOSED: Scarce equipment worth remembering';

export const KIT_ITEMS: readonly ItemDefinition[] = [
  { templateId: 'spear-bronze', name: 'Bronze-headed spear', kind: 'weapon', slot: 'main-hand', grants: ['thrust', 'set-spear'], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'blade-iron', name: 'Iron blade', kind: 'weapon', slot: 'main-hand', grants: ['cleave'], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'bow-ash', name: 'Ash self-bow', kind: 'weapon', slot: 'main-hand', grants: ['loose-arrow'], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'knife-belt', name: 'Belt knife', kind: 'tool', slot: 'belt', grants: ['quick-cut'], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'shield-round', name: 'Round shield', kind: 'shield', slot: 'off-hand', grants: ['shield-brace'], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'gambeson', name: 'Quilted gambeson', kind: 'worn', slot: 'body', grants: [], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'satchel', name: 'Canvas satchel', kind: 'container', slot: 'pack', grants: [], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'oil-flask', name: 'Flask of lamp oil', kind: 'tool', slot: 'belt', grants: [], consumable: { fact: 'burning-oil' }, unique: false, source: FOUNDATIONS },
  { templateId: 'field-dressing', name: 'Linen field dressing', kind: 'tool', slot: 'belt', grants: [], consumable: { fact: 'dressed-wound' }, unique: false, source: FOUNDATIONS },
  { templateId: 'dose-prepared', name: 'One prepared dose', kind: 'ritual-dose', slot: 'belt', grants: [], consumable: { fact: 'prepared-care' }, unique: false, source: FOUNDATIONS },
  { templateId: 'hook-sluicekeeper', name: "The Sluicekeeper's Hook", kind: 'tool', slot: 'belt', grants: ['pry-pawl'], consumable: null, unique: true, source: NAMED },
  { templateId: 'sword-nine-notches', name: 'Nine Notches', kind: 'weapon', slot: 'main-hand', grants: ['cleave'], consumable: null, unique: true, source: NAMED },
  { templateId: 'shield-bent-standard', name: 'The Bent Standard Shield', kind: 'shield', slot: 'off-hand', grants: ['shield-brace'], consumable: null, unique: true, source: NAMED },
  { templateId: 'bowl-melian', name: "Melian's Bowl", kind: 'tool', slot: 'pack', grants: ['prepare-treatment'], consumable: null, unique: true, source: NAMED },
  { templateId: 'nail-patient', name: 'The Patient Nail', kind: 'tool', slot: 'belt', grants: ['set-anchor'], consumable: null, unique: true, source: NAMED },
  { templateId: 'cloak-charcoal', name: 'Charcoal travel cloak', kind: 'worn', slot: 'body', grants: ['slip-past'], consumable: null, unique: false, source: FOUNDATIONS },
  { templateId: 'survey-tools', name: 'Surveyor\'s tool roll', kind: 'tool', slot: 'off-hand', grants: ['inspect-mechanism'], consumable: null, unique: false, source: FOUNDATIONS },
];

export const ORIGIN_SHAPES: readonly OriginShape[] = [
  { origin: 'disgraced-legionary', templates: ['shield-round', 'spear-bronze'], capabilities: [] },
  { origin: 'unpaid-sellsword', templates: ['blade-iron', 'cloak-charcoal'], capabilities: [] },
  { origin: 'fallen-household', templates: ['blade-iron', 'gambeson'], capabilities: [] },
  { origin: 'intelligence-runner', templates: ['blade-iron', 'satchel'], capabilities: [] },
  { origin: 'escaped-worker', templates: ['knife-belt', 'field-dressing'], capabilities: [] },
  { origin: 'serekh-exile', templates: ['bow-ash', 'survey-tools'], capabilities: [] },
  { origin: 'hedge-binder', templates: ['dose-prepared', 'knife-belt'], capabilities: ['rite-familiarity'] },
  { origin: 'grave-witness', templates: ['knife-belt', 'satchel'], capabilities: [] },
];

export const STARTING_KITS: readonly StartingKitDefinition[] = [
  { id: 'line-holder', label: 'Spear-and-shield line-holder', origin: 'disgraced-legionary', trainingAction: 'shield-brace', learnedCapability: 'brace-drill' },
  { id: 'opportunist', label: 'Blade-and-cloak opportunist', origin: 'unpaid-sellsword', trainingAction: 'cleave', learnedCapability: 'blade-drill' },
  { id: 'investigator', label: 'Ranged-and-tools investigator', origin: 'serekh-exile', trainingAction: 'inspect-mechanism', learnedCapability: 'fieldcraft' },
];

const capabilityIndex = new Map<string, CapabilityDefinition>(CAPABILITIES.map((capability): [string, CapabilityDefinition] => [capability.id, capability]));
const itemIndex = new Map<string, ItemDefinition>(KIT_ITEMS.map((item): [string, ItemDefinition] => [item.templateId, item]));
const originIndex = new Map<string, OriginShape>(ORIGIN_SHAPES.map((shape): [string, OriginShape] => [shape.origin, shape]));
const startingKitIndex = new Map<string, StartingKitDefinition>(STARTING_KITS.map((kit): [string, StartingKitDefinition] => [kit.id, kit]));

export function capabilityDefinition(id: CapabilityId): CapabilityDefinition {
  const found = capabilityIndex.get(id);
  if (!found) throw new Error(`Unknown capability: ${id}`);
  return found;
}

export function findCapability(id: CapabilityId): CapabilityDefinition | null {
  return capabilityIndex.get(id) ?? null;
}

export function itemDefinition(templateId: ItemTemplateId): ItemDefinition {
  const found = itemIndex.get(templateId);
  if (!found) throw new Error(`Unknown item template: ${templateId}`);
  return found;
}

export function originShape(origin: Origin): OriginShape {
  const found = originIndex.get(origin);
  if (!found) throw new Error(`Unknown origin: ${origin}`);
  return found;
}

export function startingKit(id: StartingKitId): StartingKitDefinition {
  const found = startingKitIndex.get(id);
  if (!found) throw new Error(`Unknown starting kit: ${id}`);
  return found;
}

export function provenanceKey(templateId: ItemTemplateId, serial: string): string {
  if (!SLUG.test(templateId) || !SLUG.test(serial)) throw new Error('Item template and serial must be lowercase slugs');
  return `${templateId}${PROVENANCE_SEPARATOR}${serial}`;
}

export function instanceId(templateId: ItemTemplateId, serial: string): string {
  if (!SLUG.test(templateId) || !SLUG.test(serial)) throw new Error('Item template and serial must be lowercase slugs');
  return `item:${templateId}-${serial}`;
}

/** Null when the instance is not drawn from the D01.2 slice kit. */
export function kitTemplateIdOf(item: Item): ItemTemplateId | null {
  const separator = item.provenanceKey.indexOf(PROVENANCE_SEPARATOR);
  if (separator <= 0 || separator === item.provenanceKey.length - 1) return null;
  const templateId = item.provenanceKey.slice(0, separator);
  return itemIndex.has(templateId) ? templateId : null;
}

export function instantiate(definition: ItemDefinition, serial: string, ownerId: string): Item {
  return {
    id: instanceId(definition.templateId, serial),
    provenanceKey: provenanceKey(definition.templateId, serial),
    name: definition.name,
    kind: definition.kind,
    units: 1,
    contents: [],
    allocation: { kind: 'person', id: ownerId },
  };
}
